import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import React from 'react'
import { useBoolean } from 'react-use'

import agent from 'agent'
import Icon from 'components/Icon'
import useAuth from 'hooks/use-auth'
import { trans } from 'i18n/translate'
import toast from 'lib/toast'
import { type TestSuite } from 'types/api/test-suites/test-suites'
import { messageFromResponseError } from 'utils/strings'

export const DeleteTestSuite: React.FC<{
  testSuite: TestSuite
  onChanged: () => void
}> = ({ testSuite, onChanged }) => {
  const [isModalOpen, setIsModalOpen] = useBoolean(false)
  const { currentTeamId } = useAuth()

  const handleDelete = async () => {
    try {
      await agent.delete(
        `/test-suites/${testSuite.identifier}?teamId=${currentTeamId}`
      )
      onChanged()
    } catch (err) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          trans('test_suites.list.delete.error')
        )
      })
    } finally {
      setIsModalOpen(false)
    }
  }

  return (
    <Box>
      <Button
        color="error"
        data-test-delete-test-suites={testSuite.identifier}
        data-track-action="delete-test-suites-open-dialog"
        data-track-category="test-suites"
        data-track-on="click"
        size="small"
        startIcon={<Icon icon="trash" />}
        variant="contained"
        onClick={() => setIsModalOpen(true)}
      >
        {trans('delete')}
      </Button>
      <Dialog
        aria-labelledby="alert-dialog-title"
        data-test-test-suites-delete-modal={testSuite.identifier}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle color="error" id="alert-dialog-title">
          {trans('test_suites.list.delete_modal.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {trans('test_suites.list.delete_modal.text')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            data-test-cancel-button
            data-track-action="delete-test-suites-dialog-cancel"
            data-track-category="test-suites"
            data-track-on="click"
            onClick={() => setIsModalOpen(false)}
          >
            {trans('cancel')}
          </Button>
          <Button
            autoFocus
            data-test-delete-button
            data-track-action="delete-test-suites-dialog-delete"
            data-track-category="test-suites"
            data-track-on="click"
            onClick={handleDelete}
          >
            {trans('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
